import {useEffect, useState} from 'react';
import {getAllAlertSettings} from '../../../api/settings/alert_alarm';
import Spinner from '../../common/Spinner';
import Error from '../../common/Error';
import {setTitle} from '../../../store/header/header.slice';
import {useAppDispatch, useAppSelector} from '../../../store/hooks';
import Container from '../../common/layout/Container';
import {
  alertRangeHint,
  processAlertType,
  relativeValue,
  taskAlertType
} from './constants';
import AlertSettingSwitchers from './AlertSettingSwitchers';
import AlertList from './AlertList';
import EnsurePermission from '../../common/EnsurePermission';
import permissionsInfo from '../../../constants/permissionsInfo';
import {AlertSettingsResponseProps} from './types';

const AlertSettings = () => {
  const dispatch = useAppDispatch();
  const {permissions: currentUserPermissions} = useAppSelector((state) =>
    state.permissions);
  const [processesAlertData, setProcessesAlertData] =
    useState<Nullable<AlertSettingsResponseProps>>(null);
  const [tasksAlertData, setTasksAlertData] =
    useState<Nullable<AlertSettingsResponseProps>>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [areTasksSwitching, setAreTasksSwitching] = useState(false);
  const [areProcessesSwitching, setAreProcessesSwitching] = useState(false);
  const [error, setError] = useState('');
  const [isRelativeForProcesses, setIsRelativeForProcesses] = useState(true);
  const [isRelativeForTasks, setIsRelativeForTasks] = useState(true);

  const hasTaskManagePermission =
    permissionsInfo
        .settings
        .task_progress_alert_range
        .manage
        .some((permission) =>
          currentUserPermissions.includes(permission));
  const hasProcessManagePermission =
    permissionsInfo
        .settings
        .process_progress_alert_range
        .manage
        .some((permission) =>
          currentUserPermissions.includes(permission));

  useEffect(() => {
    dispatch(setTitle('Process Settings'));
  }, []);

  const getAlertAlarmSettings = async () => {
    setIsLoading(true);

    try {
      const getProcessAlertSettings = () =>
        hasProcessManagePermission ?
        getAllAlertSettings(processAlertType) : null;
      const getTaskAlertSettings = () =>
        hasTaskManagePermission ?
          getAllAlertSettings(taskAlertType) : null;

      const [processesAlertResponse, tasksAlertResponse] =
        await Promise.all([getProcessAlertSettings(), getTaskAlertSettings()]);

      if (processesAlertResponse?.length) {
        setProcessesAlertData(processesAlertResponse);
        setIsRelativeForProcesses(processesAlertResponse[0][relativeValue]);
      }
      if (tasksAlertResponse?.length) {
        setTasksAlertData(tasksAlertResponse);
        setIsRelativeForTasks(tasksAlertResponse[0][relativeValue]);
      }
    } catch (error) {
      setError(error?.message || '');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAlertAlarmSettings();

    return () => {
      setError('');
    };
  }, []);

  return (
    <Container header="Alert/Alarm Settings">
      {isLoading ? <Spinner loadingText="Getting alert/alarm settings" /> :
        (<div className="alert-configurations-container">
          {!!processesAlertData &&
            <EnsurePermission elementPermissions={
              permissionsInfo.settings.process_progress_alert_range.manage
            }>
              <div className="configuration">
                <h2 className="subheader">Process Alarm Configuration</h2>
                <div className="switchers-container">
                  <span className="range_wrapper">
                    <span className="name">Process Progress</span>
                    <span>Alert range:</span>
                  </span>
                  <AlertSettingSwitchers
                    alertType={processAlertType}
                    isSwitching={areProcessesSwitching}
                    setIsSwitching={setAreProcessesSwitching}
                    isRelative={isRelativeForProcesses}
                    setIsRelative={setIsRelativeForProcesses}
                  />
                </div>
                <AlertList
                  data={processesAlertData}
                  setData={setProcessesAlertData}
                  alertType={processAlertType}
                  isSwitching={areProcessesSwitching}
                  isRelative={isRelativeForProcesses}
                />
                <p className="alert-range-hint">{alertRangeHint}</p>
              </div>
            </EnsurePermission>}
          {!!tasksAlertData &&
            <EnsurePermission elementPermissions={
              permissionsInfo.settings.task_progress_alert_range.manage
            }>
              <div className="configuration">
                <h2 className="subheader">Task Configuration</h2>
                <div className="switchers-container">
                  <span className="range_wrapper">
                    <span className="name">Task Progress</span>
                    <span>Alert range:</span>
                  </span>
                  <AlertSettingSwitchers
                    alertType={taskAlertType}
                    isSwitching={areTasksSwitching}
                    setIsSwitching={setAreTasksSwitching}
                    isRelative={isRelativeForTasks}
                    setIsRelative={setIsRelativeForTasks}
                  />
                </div>
                <AlertList
                  data={tasksAlertData}
                  setData={setTasksAlertData}
                  alertType={taskAlertType}
                  isSwitching={areTasksSwitching}
                  isRelative={isRelativeForTasks}
                />
                <p className="alert-range-hint">{alertRangeHint}</p>
              </div>
            </EnsurePermission>}
        </div>)
      }
      {!!error && <Error message={error}/>}
    </Container>
  );
};

export default AlertSettings;
